import React, { useState } from "react";
import styled from "styled-components";
import Button from "../components/common/Button";
import emailjs from "@emailjs/browser";
import { EMAIL_CONFIG, isEmailConfigured } from "../config/emailConfig";
import SEO from "../components/SEO";
import StructuredData, {
  LocalBusinessSchema,
} from "../components/StructuredData";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiryType: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEmailConfigured()) {
      setSubmitStatus("error");
      return;
    }
    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        inquiry_type: formData.inquiryType,
        message: formData.message,
        to_email: EMAIL_CONFIG.recipientEmail,
      };

      await emailjs.send(
        EMAIL_CONFIG.serviceId,
        EMAIL_CONFIG.templateId,
        templateParams,
        EMAIL_CONFIG.publicKey
      );

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        inquiryType: "product",
        message: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us - Get in Touch"
        description="Contact Chandelier Luxe for premium crystal chandeliers, installation, restoration, and maintenance services in Johannesburg. Call +27 84 422 9825 or email info@chandelierluxe.co.za"
        keywords="chandelier contact, chandelier services Johannesburg, crystal lighting contact, chandelier installation quote, chandelier restoration inquiry"
        url="/contact"
      />
      <StructuredData data={LocalBusinessSchema} />
      <ContactContainer>
        <h1>Contact Us</h1>

        {submitStatus === "success" && (
          <SuccessMessage>
            Thank you for your message! We'll get back to you soon.
          </SuccessMessage>
        )}

        {submitStatus === "error" && (
          <ErrorMessage>
            {!isEmailConfigured()
              ? "Email is not configured. Please add REACT_APP_EMAILJS_PUBLIC_KEY to your .env file (see .env.example)."
              : "Sorry, there was an error sending your message. Please try again or contact us directly."}
          </ErrorMessage>
        )}

        <ContactForm onSubmit={handleSubmit}>
          <FormGroup>
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="phone">Phone *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="inquiryType">Inquiry Type</label>
            <select
              id="inquiryType"
              name="inquiryType"
              value={formData.inquiryType}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select Inquiry
              </option>
              <option value="product">Product Interest</option>
              <option value="installation">Installation Service</option>
              <option value="restroration">Restoration Service</option>
              <option value="accessories">Accessories Service</option>
              <option value="other">Other</option>
            </select>
          </FormGroup>
          <FormGroup>
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              required
            ></textarea>
          </FormGroup>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Sending..." : "Send Message"}
          </Button>
        </ContactForm>
      </ContactContainer>
    </>
  );
};

const ContactContainer = styled.div`
  max-width: ${(props) => props.theme.maxWidth};
  margin: 6rem auto 2rem;
  padding: 0 2rem;

  h1 {
    text-align: center;
    margin-bottom: 3rem;
  }
`;

const ContactForm = styled.form`
  max-width: 600px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: ${(props) => props.theme.colors.text};
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.8rem;
    background: ${(props) => props.theme.colors.secondary};
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: ${(props) => props.theme.colors.text};
    border-radius: 4px;

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.accent};
    }
  }
`;

const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
`;

export default Contact;
